import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import { AnalyticsEvents } from "../AnalyticsEvents";
import { AnalyticsProperties, AnalyticsTraits } from "../types";

type InAppMessageEvent = {
  type: string;
  data?: {
    deliveryId?: string;
    messageId?: string;
    actionName?: string;
    actionValue?: string;
    errorMessage?: string;
  };
};

/**
 * Customer.io's push metrics endpoint. This is deliberately NOT the CDP/track pipeline: a CDP `track`
 * call carrying the delivery id lands as a custom event and does not move the delivery's own `opened`
 * metric, so broadcast branches like "have ever opened the push" never see it. Reporting the metric
 * here is what makes a push open visible to Customer.io reporting and to workflow conditions.
 *
 * Unauthenticated by design — it is the endpoint Customer.io's own mobile SDKs call, authorized by the
 * delivery id + delivery token that Customer.io itself put in the push payload.
 */
const CUSTOMER_IO_PUSH_METRICS_ENDPOINT = "https://track.customer.io/api/v1/push/events";

export type CustomerIoPushMetric = "delivered" | "opened" | "converted";

export class CustomerIoAdapter {
  public cioanalytics?: AnalyticsBrowser;
  private onMessageAction?: (actionName: string, actionValue: string) => void;
  private segmentTrack?: (event: string, properties?: AnalyticsProperties) => void;

  constructor(
    writeKey?: string,
    siteId?: string,
    options?: {
      onMessageAction?: (actionName: string, actionValue: string) => void;
      segmentTrack?: (event: string, properties?: AnalyticsProperties) => void;
    },
  ) {
    this.onMessageAction = options?.onMessageAction;
    this.segmentTrack = options?.segmentTrack;

    if (writeKey && siteId) {
      // Use cioanalytics variable name to avoid conflict with Segment
      this.cioanalytics = AnalyticsBrowser.load(
        { writeKey },
        {
          integrations: {
            "Customer.io In-App Plugin": {
              siteId,
              // Enable anonymous in-app messages
              anonymousInApp: true,
              // Handle in-app message events
              events: ((event: InAppMessageEvent) => {
                this.handleInAppMessageEvent(event);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              }) as any,
            },
          },
        },
      );

      if (import.meta.env.DEV) {
        console.log("Customer.io initialized for in-app messaging");
      }
    } else {
      console.warn(`Failed to initialize customer.io`);
    }
  }

  private handleInAppMessageEvent(event: InAppMessageEvent): void {
    const { type, data } = event;

    // Track all in-app message events to Segment for analytics
    const eventName = type.replace("in-app:", "Customer.io ");
    const properties = {
      deliveryId: data?.deliveryId,
      messageId: data?.messageId,
      actionName: data?.actionName,
      actionValue: data?.actionValue,
      errorMessage: data?.errorMessage,
    };

    this.segmentTrack?.(eventName, properties);

    // Handle message actions for custom routing/navigation
    if (type === "in-app:message-action" && data?.actionName && data?.actionValue) {
      this.onMessageAction?.(data.actionName, data.actionValue);
    }
  }

  page(name: string, properties?: AnalyticsProperties): void {
    // Call page for in-app message page rules to work correctly in SPA
    this.cioanalytics?.page(name, properties);
  }

  identify(userId: string, traits?: AnalyticsTraits): void {
    // Identify users for in-app message targeting
    this.cioanalytics?.identify(userId, traits);
  }

  async deletePushDevice(userId: string, deviceToken: string): Promise<void> {
    if (!this.cioanalytics) {
      return;
    }

    await this.cioanalytics.identify(userId);
    await this.cioanalytics.track(
      AnalyticsEvents.DEVICE_DELETED,
      {},
      {
        context: {
          device: {
            token: deviceToken,
          },
        },
      },
    );
  }

  reset(): void {
    // Clear the user identity to disassociate device from user session
    this.cioanalytics?.reset();
  }

  /**
   * Reports a push metric against the Customer.io delivery that produced the notification.
   *
   * Both ids come from the push payload Customer.io sent (`CIO-Delivery-ID` / `CIO-Delivery-Token`);
   * a notification without them did not originate from Customer.io, so there is no delivery to
   * attribute and we do not call out.
   */
  async reportPushMetric(
    metric: CustomerIoPushMetric,
    deliveryId?: string,
    deliveryToken?: string,
  ): Promise<void> {
    if (!deliveryId || !deliveryToken) {
      return;
    }

    try {
      await fetch(CUSTOMER_IO_PUSH_METRICS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          delivery_id: deliveryId,
          device_id: deliveryToken,
          event: metric,
          timestamp: Math.floor(Date.now() / 1000),
        }),
      });
    } catch (error) {
      // Never let a metrics beacon break the tap that triggered it — the user is mid-navigation.
      // Logged rather than swallowed so a systematic reporting outage is visible.
      console.error(`[RMD]: failed to report Customer.io push metric "${metric}"`, error);
    }
  }
}
